import React, { useState, useEffect, useRef, useCallback } from "react";

const LandingPage = () => {
  const [metrics, setMetrics] = useState({
    metric1: { value: 0, target: 95, suffix: "%" },
    metric2: { value: 0, target: 10000, suffix: "k+" },
    metric3: { value: 0, target: 1000, suffix: ",000+" },
  });
  const metricsRef = useRef(null);
  const metricsObserved = useRef(false);

  // Responsive + Animation Styles for the entire page
  const styles = `
    /* --- FONT & GLOBAL STYLES --- */
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;700;800;900&display=swap');

    :root {
        --color-primary: #0F172A;
        --color-secondary: #DC2626;
        --color-accent: #B91C1C;
        --color-background: #F1F5F9;
        --color-text: #1f2937;
        --color-text-light: #f8fafc;
    }

    .page-wrapper {
        font-family: 'Inter', sans-serif;
        background-color: var(--color-background);
        color: var(--color-text);
        overflow-x: hidden; /* Prevents horizontal scroll */
    }

    .container {
        max-width: 1280px;
        margin: 0 auto;
        padding: 0 1rem; /* Base padding for mobile */
    }

    .container .color-text{
        color:  #b62626 !important;
    }

    /* --- GENERAL SECTION STYLING --- */
    .section { padding: 4rem 1rem; }

    .section-header {
        text-align: center;
        margin-bottom: 2.5rem;
    }

    .section-header h2 {
        font-size: clamp(1.75rem, 4vw, 2.5rem);
        font-weight: 800;
        color: var(--color-primary);
        margin-bottom: 0.75rem;
    }

    .section-header h3 {
        font-size: clamp(1.5rem, 4vw, 2rem);
        font-weight: 800;
        color: var(--color-secondary);
        margin-bottom: 2rem;
    }

    .section-header p {
        font-size: 1.125rem;
        color: #4b5563;
        max-width: 800px;
        margin: 0 auto;
    }

    /* --- HERO SECTION --- */
    .hero {
        padding: 4rem 1rem;
        background: linear-gradient(135deg, #0F172A 0%, #172554 100%);
        color: var(--color-text-light);
        text-align: center;
    }
    .hero h1 { font-size: clamp(2.25rem, 6vw, 4.5rem); font-weight: 800; margin-bottom: 1rem; line-height: 1.2; }
    .hero h2 { font-size: clamp(1.2rem, 3vw, 2.25rem); color: #d1d5db; margin-bottom: 1.25rem; }
    .cta-group { display: flex; flex-direction: column; align-items: center; gap: 1rem; }

    /* --- BUTTONS --- */
    .btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
        border-radius: 10px;
        font-size: 14px;
        font-weight: 600;
        transition: all 0.3s ease-in-out;
        text-decoration: none;
        box-shadow: 0 8px 15px -3px rgba(0, 0, 0, 0.1);
        border: none;
        cursor: pointer;
    }
    .btn:hover { transform: scale(1.05); }
    .btn-primary { background-color: var(--color-primary); color: white; }
    .btn-secondary { background-color: var(--color-secondary); color: white; }
    .btn-secondary:hover { background-color: var(--color-accent); }
    .btn-free { border: 3px solid var(--color-secondary); color: var(--color-primary); background-color: white; }

    /* --- PRICING & CARDS --- */
    .pricing-grid { display: grid; grid-template-columns: 1fr; gap: 2rem;}
    .card {
        background: white;
        border-radius: 1.5rem;
        padding: 2rem;
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
        transition: all 0.3s ease;
        text-align: center;
    }
    .card:hover { transform: translateY(-5px); box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1); }
    .card-featured { background-color: var(--color-primary); color: var(--color-text-light); border-top: 6px solid var(--color-secondary); }
    .card-featuredwhite {border-top: 6px solid var(--color-secondary); }
    .card h3 { font-size: 1.5rem; font-weight: 700; margin-bottom: 1rem; }
    .card-price { font-size: 3rem; font-weight: 800; color: var(--color-secondary); margin-bottom: 1rem; }
    .card ul { list-style: none; padding: 0; margin: 1.5rem 0; text-align: left; }
    .card li { margin-bottom: 0.5rem; }
    .img-container { width: 100%; max-width: 1024px; margin: 0 auto 3rem auto; }
    .img-container img { border-radius: 20px; width: 100%; height: auto; display: block; }

    /* --- METRICS SECTION --- */
    .metrics-section { background-color: #111827; color: white; padding: 3rem 1rem; text-align: center; }
    .metrics-grid { display: grid; grid-template-columns: 1fr; gap: 2rem; }
    .metric-item p:first-child { font-size: clamp(2.5rem, 8vw, 3.75rem); font-weight: 800; color: var(--color-secondary); }

    /* --- FEATURE SECTION (USP) --- */
    .feature-grid {
        display: grid; grid-template-columns: 1fr; gap: 2rem; align-items: center;
        padding: 2rem; border-radius: 1rem; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1); margin-bottom: 3rem;
    }
    .feature-grid img { width: 100%; border-radius: 0.75rem; }
    .feature-grid h3 { font-size: clamp(1.5rem, 4vw, 2rem); font-weight: 800; margin-bottom: 1rem; }
    .feature-grid p { font-size: 1.1rem; }
    .feature-dark { background-color: var(--color-primary); color: white; border-top: 8px solid var(--color-secondary); }
    .feature-light { background-color: white; border-top: 8px solid var(--color-accent); }
    .feature-dark h3 { color: var(--color-secondary); }
    .feature-dark p { color: #d1d5db; }
    .feature-light h3 { color: var(--color-primary); }
    .feature-light p { color: #4b5563; }

    /* --- SUCCESS TIPS SECTION --- */
    .tips-grid { display: grid; grid-template-columns: 1fr; gap: 1.5rem; text-align: left; }
    .tip-card {
        background: #fff; padding: 1.5rem; border-radius: 1rem;
        box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); border-left: 4px solid var(--color-accent);
    }
    .tip-card p { font-weight: 700; font-size: 1.25rem; color: var(--color-primary); margin-bottom: 0.5rem;}
    .tip-card ul { list-style: disc; padding-left: 1.5rem; color: #4b5563; }

    /* --- 4-STEP PROCESS SECTION --- */
    .process-section { background-color: #0c1221f2; color: white; }
    .process-grid { display: grid; grid-template-columns: 1fr; gap: 2rem; }
    .step-icon {
        background: rgba(255, 255, 255, 0.1); backdrop-filter: blur(10px);
        border: 1px solid rgba(255, 255, 255, 0.2); padding: 1.5rem; border-radius: 1rem;
        text-align: center; transition: transform 0.3s ease; border-bottom: 4px solid;
    }
    .step-icon:hover { transform: scale(1.03); }
    .step-icon div { font-size: 3rem; font-weight: 800; margin-bottom: 0.75rem; }
    .step-icon h3 { font-size: 1.25rem; font-weight: 700; margin-bottom: 0.5rem; }
    .step-1-color, .step-3-color { color: var(--color-secondary); }
    .step-2-color, .step-4-color { color: var(--color-accent); }
    .step-border-secondary { border-color: var(--color-secondary); }
    .step-border-accent { border-color: var(--color-accent); }

    /* --- TESTIMONIALS & FOOTER --- */
    .testimonials-grid { display: grid; grid-template-columns: 1fr; gap: 2rem; }
    .testimonial-card {
        background-color: white; padding: 1.5rem; border-radius: 1rem;
        border-left: 4px solid var(--color-secondary); box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1); transition: all 0.3s ease;
    }
    .testimonial-card:hover { transform: translateY(-5px); box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1); }
    .testimonial-card p:first-child { font-style: italic; color: #4b5563; margin-bottom: 1rem; }
    .testimonial-card p:last-child { font-weight: 700; color: var(--color-primary); }
    .footer { background-color: #111827; color: #6b7280; padding: 1.5rem 0; text-align: center; }

    /* --- RESPONSIVE BREAKPOINTS --- */
    @media (min-width: 768px) {
        .container { padding: 0 2rem; }
        .hero { padding: 6rem 1rem; }
        .btn { padding: 1.25rem 2.5rem; }
        .cta-group { flex-direction: row; justify-content: center; }
        .metrics-grid, .tips-grid { grid-template-columns: repeat(3, 1fr); }
        .pricing-grid { grid-template-columns: repeat(2, 1fr); }
        .feature-grid { grid-template-columns: repeat(2, 1fr); }
        .testimonials-grid { grid-template-columns: repeat(2, 1fr); }
        .process-grid { grid-template-columns: repeat(2, 1fr); }
    }
    @media (min-width: 1024px) {
        .pricing-grid, .testimonials-grid { grid-template-columns: repeat(3, 1fr); }
        .process-grid { grid-template-columns: repeat(4, 1fr); }
        .card-featured { transform: scale(1.05); }
        .card-featured:hover { transform: scale(1.05) translateY(-5px); }
    }

    /* --- ANIMATIONS --- */
    @keyframes pulse-once { 0% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.7); } 50% { box-shadow: 0 0 0 15px rgba(220, 38, 38, 0); } 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0); } }
    .animate-pulse-once { animation: pulse-once 1.5s ease-out 1; }
    @keyframes shake { 0%, 100% { transform: translateX(0); } 10%, 30%, 50%, 70%, 90% { transform: translateX(-3px); } 20%, 40%, 60%, 80% { transform: translateX(3px); } }
    .animate-shake-cycle { animation: shake 0.3s ease-in-out 3, shake 4s ease-in-out 3s infinite alternate; }
  `;

  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.innerHTML = styles;
    document.head.appendChild(styleElement);
    return () => document.head.removeChild(styleElement);
  }, [styles]);

  useEffect(() => {
    const currentMetricsRef = metricsRef.current;
    if (!currentMetricsRef) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !metricsObserved.current) {
          metricsObserved.current = true;
          const duration = 2000;
          let startTime;
          const animateMetric = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const elapsed = timestamp - startTime;
            const progress = Math.min(elapsed / duration, 1);
            setMetrics((prev) => {
              const newMetrics = {};
              for (const key in prev) {
                newMetrics[key] = {
                  ...prev[key],
                  value: prev[key].target * progress,
                };
              }
              return newMetrics;
            });
            if (progress < 1) requestAnimationFrame(animateMetric);
          };
          requestAnimationFrame(animateMetric);
          observer.unobserve(currentMetricsRef);
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(currentMetricsRef);
    return () => observer.disconnect();
  }, []);

  const formatNumber = useCallback((num, suffix) => {
    if (suffix === "%") return Math.floor(num) + suffix;
    if (suffix === "k+") return Math.floor(num / 100) / 10 + "k+";
    if (suffix === ",000+")
      return (Math.floor(num) / 1000).toLocaleString() + ",000+";
    return Math.floor(num).toLocaleString();
  }, []);

  return (
    <div className="page-wrapper">
      {/* HERO */}
      <section className="hero">
        <div className="container">
          <h1>The Grand Launch Is Here.</h1>
          <h2 className="color-text">Practice. Perfect. Pass.</h2>
          <h2 className="color-text">
            Master the CFA Exam with
            <span style={{ color: "#dc2626", fontWeight: 800 }}> EduMocks.</span>
          </h2>
          <p style={{ fontSize: "18px" }}>
            Introducing the only platform built around your schedule: Buy,
            Schedule Your Test Instantly,
            <br /> and Get Detailed Analytics.
          </p>
          <div className="cta-group">
            <a
              href="https://edumocks.com/studSignup"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary animate-pulse-once animate-shake-cycle"
            >
              Start Mocks at Just{" "}
              <span style={{ fontSize: "1.875rem", marginLeft: "0.25rem" }}>
                $1
              </span>
            </a>
          </div>
          <p
            style={{
              marginTop: "1.5rem",
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "#d1d5db",
            }}
          >
            Limited Availability at Launch Prices. Don't Miss Out.
          </p>
        </div>
      </section>

      {/* PRICING */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2>The Grand Launch Product Suite: CFA Level 1</h2>
            <p>
              Access top-tier preparation material with our special introductory
              pricing. Preparation starts from just $1!
            </p>
          </div>

          <div className="img-container">
            <img
              src="https://eduinvest.ae/wp-content/uploads/2025/10/Screenshot-2025-10-04-at-10.29.16-PM.png"
              alt="EduMocks Book Test screen"
              className="image-css"
            />
          </div>

          {/* pricing-grid */}
          <div className="pricing-grid">
            <div className="card card-featured">
              <h3
                style={{
                  fontSize: "18px",
                }}
              >
                Ultimate Preparation
              </h3>
              <h3>Complete Bundle</h3>
              <p className="card-price">
                $10
                <span
                  style={{
                    textDecoration: "line-through",
                    color: "#9ca3af",
                    fontSize: "1.5rem",
                    marginLeft: "0.5rem",
                  }}
                >
                  $99
                </span>
              </p>
              <p>Biggest Savings!</p>
              <ul
                style={{
                  listStyle: "disc",
                }}
              >
                <li>10 Topic-Wise Mocks</li>
                <li>1 Full Exam Simulation</li>
                <li>All Analytical & Retest Features</li>
                <li>Exam Schedule Updates</li>
              </ul>
              <a
                href="https://edumocks.com/studSignup"
                className="btn btn-secondary"
              >
                Get The Ultimate Prep Kit for $10
              </a>
            </div>
            <div className="card card-featuredwhite">
              <h3
                style={{
                  fontSize: "18px",
                  color: "#b1b4bc",
                }}
              >
                Essential Focus
              </h3>
              <h3>Topic Mock (x1)</h3>
              <p className="card-price">
                $1
                <span
                  style={{
                    textDecoration: "line-through",
                    color: "#9ca3af",
                    fontSize: "1.5rem",
                    marginLeft: "0.5rem",
                  }}
                >
                  $9
                </span>
              </p>
              <p
                style={{
                  color: "#dc2626",
                  fontSize: "1rem",
                  fontWeight: "600",
                }}
              >
                Limited-Time 89% OFF!
              </p>
              <ul
                style={{
                  listStyle: "disc",
                }}
              >
                <li>Single Topic Deep Dive</li>
                <li>Schedule Anytime Flexibility</li>
                <li>Instant Solutions & Analysis</li>
                <li>X Full Exam Simulation (Not Incl.)</li>
              </ul>
              <a
                href="https://edumocks.com/studSignup"
                className="btn btn-secondary"
              >
                Buy Now for $1
              </a>
            </div>
            <div className="card card-featuredwhite">
              <h3
                style={{
                  fontSize: "18px",
                  color: "#b1b4bc",
                }}
              >
                Exam Readiness
              </h3>
              <h3>Full Mock Exam (x1)</h3>
              <p className="card-price">
                $1
                <span
                  style={{
                    textDecoration: "line-through",
                    color: "#9ca3af",
                    fontSize: "1.5rem",
                    marginLeft: "0.5rem",
                  }}
                >
                  $10
                </span>
              </p>
              <p
                style={{
                  color: "#dc2626",
                  fontSize: "1rem",
                  fontWeight: "600",
                }}
              >
                Experience Real Exam Simulation!
              </p>
              <ul
                style={{
                  listStyle: "disc",
                }}
              >
                <li>Full-Length Timed Simulation</li>
                <li>Schedule Anytime Flexibility</li>
                <li>Detailed Analytics Included</li>
                <li>Expert Strategy Access</li>
              </ul>
              <a
                href="https://edumocks.com/studSignup"
                className="btn btn-secondary"
              >
                Claim Full Mock for $1
              </a>
            </div>
          </div>
          <div
            style={{
              width: "100%",
              textAlign: "center",
              margin: "20px",
              padding: "20px",
            }}
          >
            <a
              href="https://edumocks.com/studSignup"
              className="btn btn-secondary"
              style={{
                textAlign: "center",
                fontSize: "15px;",
              }}
            >
              Secure Your Launch Price Now!
            </a>
          </div>
        </div>
      </section>

      {/* METRICS */}
      <section className="metrics-section" ref={metricsRef}>
        <div className="container">
          <div className="metrics-grid">
            <div className="metric-item">
              <p>
                {formatNumber(metrics.metric1.value, metrics.metric1.suffix)}
              </p>
              <p>Target Passing Rate</p>
            </div>
            <div className="metric-item">
              <p>
                {formatNumber(metrics.metric2.value, metrics.metric2.suffix)}
              </p>
              <p>Unique Questions Answered</p>
            </div>
            <div className="metric-item">
              <p>
                {formatNumber(metrics.metric3.value, metrics.metric3.suffix)}
              </p>
              <p>Satisfied Candidates</p>
            </div>
          </div>
        </div>
      </section>

      {/* CFA Exam Imperative & Features Section */}
      <section id="usp" className="section">
        <div className="container">
          <div className="section-header">
            <h2>The CFA Exam Imperative: Practice Till You Pass!</h2>
            <p>
              The CFA Level 1 exam is a challenge, and mock exams aren't just a
              nice-to-have—they're essential. Candidates who regularly simulate
              the exam outperform those who only revise.
            </p>
          </div>
          <div className="feature-grid feature-dark">
            <div style={{ order: 2 }}>
              <img
                src="https://eduinvest.ae/wp-content/uploads/2025/10/Screenshot-2025-10-05-at-12.32.05-PM-scaled.png"
                alt="EduMocks Schedule Test screen"
              />
            </div>
            <div style={{ order: 1 }}>
              <h3>Schedule Instantly. Practice Topic-by-Topic.</h3>
              <p>
                You have complete control over your exam prep. Our "Schedule
                Test" interface allows you to book quick practice slots or full
                simulations immediately.
              </p>
            </div>
          </div>
          <div className="feature-grid feature-light">
            <div style={{ order: 1 }}>
              <img
                src="https://eduinvest.ae/wp-content/uploads/2025/10/Screenshot-2025-10-04-at-10.31.09-PM-scaled.png"
                alt="EduMocks Dashboard showing performance"
              />
            </div>
            <div style={{ order: 2 }}>
              <h3>Performance Analytics: Pinpoint Your Grade.</h3>
              <p>
                Stop guessing. Our detailed dashboard gives you immediate grades
                and a visual score breakdown to guide your next study session.
              </p>
            </div>
          </div>
          <div className="text-center" style={{ marginTop: "2.5rem" }}>
            <a
              href="https://edumocks.com/studSignup"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary animate-shake-cycle"
            >
              Get Instant Access & Start Scheduling
            </a>
          </div>
          <div className="section-header" style={{ marginTop: "4rem" }}>
            <h3>Tips for CFA Success: Fine-Tune Your Exam Strategy</h3>
            <div className="tips-grid">
              <div className="tip-card">
                <p>Target Score & Volume</p>
                <ul>
                  <li>Aim for 75% or higher on mocks.</li>
                  <li>Take at least 5 full-length mock exams.</li>
                </ul>
              </div>
              <div className="tip-card">
                <p>Time Management</p>
                <ul>
                  <li>Spend no more than 1.5 minutes per question.</li>
                  <li>Do easier questions first, then cycle back.</li>
                </ul>
              </div>
              <div className="tip-card">
                <p>Review Discipline</p>
                <ul>
                  <li>Attempt all questions before reviewing solutions.</li>
                  <li>Use analytics to build resilience.</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="text-center" style={{ marginTop: "3rem" }}>
            <a
              href="https://edumocks.com/studSignup"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary animate-shake-cycle"
            >
              Unlock All Success Tips & Mocks
            </a>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <div
        style={{
          padding: "2.5rem 0",
          backgroundColor: "rgba(185, 28, 28, 0.1)",
          textAlign: "center",
        }}
      >
        <a
          href="https://edumocks.com/studSignup"
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-secondary animate-shake-cycle"
        >
          Ready to Be Our Next Success Story? Sign Up!
        </a>
      </div>

      {/* 4-Step Process Section */}
      <section id="process" className="section process-section">
        <div className="container">
          <div className="section-header">
            <h2 style={{ color: "var(--color-secondary)" }}>
              Seamless Success: Your Instant 4-Step Journey
            </h2>
            <p style={{ color: "#d1d5db" }}>
              The most flexible way to prepare: Buy, Schedule, Get Results -
              Instantly.
            </p>
          </div>
          <div className="process-grid">
            <div className="step-icon step-border-secondary">
              <div className="step-1-color">1</div>
              <h3>Choose Your Product</h3>
            </div>
            <div className="step-icon step-border-accent">
              <div className="step-2-color">2</div>
              <h3>Secure Your Launch Price</h3>
            </div>
            <div className="step-icon step-border-secondary">
              <div className="step-3-color">3</div>
              <h3>Schedule Your Test</h3>
            </div>
            <div className="step-icon step-border-accent">
              <div className="step-4-color">4</div>
              <h3>Get Instant Solutions</h3>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="section">
        <div className="container">
          <div className="section-header">
            <h2>Success Stories: Built on Trust</h2>
            <p>
              Feedback from CFA candidates who leveraged the power of timely
              practice.
            </p>
          </div>
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <p>
                "The mock tests were scary accurate to the real exam. The
                detailed analytics showed me exactly where to focus."
              </p>
              <p>Amit Desai, CFA Aspirant</p>
            </div>
            <div className="testimonial-card">
              <p>
                "Coupled with the timed mocks and instant solutions, this
                platform is a game-changer. Highly recommend!"
              </p>
              <p>Sophia Williams, Candidate</p>
            </div>
            <div className="testimonial-card">
              <p>
                "The flexibility to schedule tests anytime allowed me to target
                my weakest areas efficiently. It's the most time-saving prep
                tool I used."
              </p>
              <p>Rajiv Kumar, Satisfied Student</p>
            </div>
          </div>
        </div>
      </section>

      {/* Simple Footer */}
      <footer className="footer">
        <div className="container">
          <p>&copy; 2025 EduMocks. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
